import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { getProduct, SIZES, eur } from "@/lib/shop";
import { MEDIA } from "@/lib/collection";

const ART = "https://customer-assets-jt897jd0.emergentagent.net/job_the-x-goes-here/artifacts/";

const ARTICLES = [
  {
    slug: "at-home-haussmann",
    kind: { en: "At home with…", fr: "At home with…" },
    title: {
      en: "At home with the X — a Haussmann flat in the 9th",
      fr: "At home with the X — un haussmannien dans le 9e",
    },
    image: ART + "anaj0dyf_ChatGPT%20Image%20Aug%2025%2C%202026%2C%2011_53_31%20AM.png",
    excerpt: {
      en: "Marble fireplace, herringbone parquet, and a Banksy that was never there. A conversation about the empty space above the sofa.",
      fr: "Cheminée en marbre, parquet à bâtons rompus, et un Banksy qui n'a jamais existé. Conversation autour du vide au-dessus du canapé.",
    },
    body: [
      {
        en: "The flat is everything a Parisian flat promises: mouldings, a marble fireplace, light falling from the courtyard at four in the afternoon. And above the low sofa, where a painting would normally fight for attention — a cross, and four words.",
        fr: "L'appartement tient toutes les promesses du haussmannien : moulures, cheminée en marbre, lumière de cour à seize heures. Et au-dessus du canapé bas, là où un tableau réclamerait l'attention — une croix, et quatre mots.",
      },
      {
        en: "“Guests always ask which Banksy it is,” laughs the owner. “I tell them it's the one I can afford: the idea of one.” The room does the rest — the walnut console, the tall windows, the quiet. The absence has better taste than most presences.",
        fr: "« Les invités demandent toujours quel Banksy c'est, rit le propriétaire. Je réponds : celui que je peux m'offrir — l'idée d'un Banksy. » La pièce fait le reste — la console en noyer, les hautes fenêtres, le calme. L'absence a meilleur goût que la plupart des présences.",
      },
      {
        en: "That's the whole trick of the X: it doesn't decorate a room, it asks it a question. What would you hang here, if you could hang anything?",
        fr: "C'est tout le tour du X : il ne décore pas une pièce, il lui pose une question. Qu'accrocheriez-vous ici, si vous pouviez tout accrocher ?",
      },
    ],
    note: { en: "Editorial launch story — styled interior.", fr: "Article de lancement — intérieur stylisé." },
    products: ["banksy", "arsham", "picasso"],
  },
  {
    slug: "mural-foundation-hall",
    kind: { en: "Commission case study", fr: "Étude de cas — commission" },
    title: {
      en: "Monumental: an X for a foundation hall",
      fr: "Monumental : un X pour le hall d'une fondation",
    },
    image: MEDIA.institution,
    excerpt: {
      en: "Six metres of wall, one gesture. How the empty cross scales from a print to a statement — and what it asks of a public space.",
      fr: "Six mètres de mur, un seul geste. Comment la croix vide passe du tirage au manifeste — et ce qu'elle demande à un espace public.",
    },
    body: [
      {
        en: "At print scale, the X is intimate — a quiet joke between you and the wall. At six metres, the joke becomes a proposition. Visitors entering the hall stop walking. They look up. They name, one after another, the painting they think should be there.",
        fr: "À l'échelle du tirage, le X est intime — une blague silencieuse entre vous et le mur. À six mètres, la blague devient une proposition. Les visiteurs s'arrêtent en entrant. Ils lèvent les yeux. Ils nomment, l'un après l'autre, le tableau qui devrait être là.",
      },
      {
        en: "That naming is the artwork. A foundation doesn't buy a cross — it buys the conversation the cross starts, every single day, at zero running cost.",
        fr: "Cette nomination est l'œuvre. Une fondation n'achète pas une croix — elle achète la conversation qu'elle déclenche, chaque jour, à coût nul.",
      },
      {
        en: "Murals are measured, drawn and painted by hand on site, in the colours of the artist the wall is waiting for. The wall keeps its sovereignty; the masterpiece stays imaginary; the conversation is very real.",
        fr: "Les fresques sont mesurées, tracées et peintes à la main sur place, dans les couleurs de l'artiste que le mur attend. Le mur garde sa souveraineté ; le chef-d'œuvre reste imaginaire ; la conversation, elle, est bien réelle.",
      },
    ],
    note: { en: "Concept visual — no project is implied.", fr: "Visuel conceptuel — aucun projet réel n'est impliqué." },
    products: ["monet", "picasso", "newman"],
  },
];

export function JournalIndex() {
  const { t, lang } = useLanguage();
  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <Nav solid />
      <main className="pt-24 md:pt-32 pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">{t("journal.kicker")}</span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#111111]">
            {t("journal.title.a")}
            <span className="italic">{t("journal.title.i")}</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg font-light leading-relaxed text-[#111111]/70">
            {t("journal.intro")}
          </p>

          <div className="mt-16 space-y-20">
            {ARTICLES.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.05}>
                <Link
                  to={`/journal/${a.slug}`}
                  data-testid={`journal-card-${a.slug}`}
                  className="group grid grid-cols-1 lg:grid-cols-12 gap-8 items-end"
                >
                  <div className="lg:col-span-8 overflow-hidden border gallery-line bg-white">
                    <img src={a.image} alt={a.title.en} loading="lazy"
                      className="aspect-[16/9] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.02]" />
                  </div>
                  <div className="lg:col-span-4">
                    <span className="text-xs uppercase tracking-[0.25em] text-[#a89f8e]">
                      {a.kind[lang] || a.kind.en}
                    </span>
                    <h2 className="mt-4 font-display text-2xl md:text-3xl leading-tight tracking-tight text-[#111111]">
                      {a.title[lang] || a.title.en}
                    </h2>
                    <p className="mt-4 text-sm font-light leading-relaxed text-[#111111]/60">
                      {a.excerpt[lang] || a.excerpt.en}
                    </p>
                    <span className="link-underline mt-5 inline-block text-sm font-light text-[#111111]">
                      {t("journal.read")}
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function JournalArticle() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const a = ARTICLES.find((x) => x.slug === slug);
  const from = Math.min(...SIZES.map((s) => s.prices.unframed));

  if (!a) {
    return (
      <div className="bg-[#faf8f5] min-h-screen">
        <Nav solid />
        <div className="pt-40 text-center font-light text-[#111111]/70">{t("product.notFound")}</div>
      </div>
    );
  }

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <Nav solid />
      <main className="pt-20 md:pt-24 pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Link to="/journal" className="inline-flex items-center gap-2 text-sm font-light text-[#111111]/70 hover:text-[#111111]">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("journal.kicker")}
          </Link>
          <div className="mt-10 max-w-3xl">
            <span className="text-xs uppercase tracking-[0.25em] text-[#a89f8e]">{a.kind[lang] || a.kind.en}</span>
            <h1 className="mt-4 font-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#111111]" data-testid="journal-article-title">
              {a.title[lang] || a.title.en}
            </h1>
          </div>
          <div className="mt-10 border gallery-line overflow-hidden bg-white">
            <img src={a.image} alt={a.title.en} className="w-full aspect-[16/8] object-cover" />
          </div>
          <p className="mt-3 text-xs font-light text-[#111111]/40">{a.note[lang] || a.note.en}</p>

          <div className="mt-12 max-w-2xl space-y-6">
            {a.body.map((p, i) => (
              <p key={i} className="text-base md:text-lg font-light leading-relaxed text-[#111111]/75">
                {p[lang] || p.en}
              </p>
            ))}
          </div>

          {/* Shoppable products */}
          <div className="mt-20 border-t gallery-line pt-12" data-testid="journal-products">
            <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">{t("journal.shopThe")}</span>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 border-t border-l gallery-line">
              {a.products.map((pid) => {
                const p = getProduct(pid);
                if (!p) return null;
                return (
                  <Link key={pid} to={`/shop/${p.id}`} data-testid={`journal-product-${pid}`}
                    className="group border-r border-b gallery-line">
                    <div className="overflow-hidden bg-white">
                      <img src={p.prints[p.cardColor || "bicolor"]} alt={p.tagline} loading="lazy"
                        className="aspect-[4/5] w-full object-contain p-8 transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]" />
                    </div>
                    <div className="flex items-end justify-between gap-4 px-6 py-5">
                      <h3 className="font-display italic text-lg tracking-tight text-[#111111]">{p.name}</h3>
                      <span className="text-sm font-light text-[#111111]/70">
                        {t("shop.from")} {eur(from, lang)}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
